/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
    useMutation, useQuery, useReactiveVar, gql,
} from '@apollo/client';
import { errorService, analyticService } from 'global/services';

import {
    LOCATION_MSK,
    LOCATION_NIZ,
    LOCATION_SPB,
    LOCATION_KZN,
    LOCATION_TUL,
    LOCATION_YAR,
    LOCATION_VLA,
    LOCATION_VOR,
    LOCATION_RYA,
    LOCATION_KLU,
    LOCATION_TVE,
    NIZ_CODES,
    SPB_CODES,
    KZN_CODES,
} from 'app/const/location';
import { FETCH_DELIVERY_ADDRESS_SESSION } from 'app/graphql/network/deliveryAddress.query';
import FETCH_AVAILABLE_MENU_DATES from 'app/graphql/network/menuDates';
import {
    isGlobalLoadingVar, paymentMethodVar, regionSubdivisionVar, subscriptionTypeVar,
} from 'app/apollo/reaction';
import { BASKET_PAGE_ROOT_QUERY_INITIAL_COPY } from 'app/hooks/accessors/useBasketData';
import { MUTATE_SHIPPING_ADDRESS } from './graphql/locationProvider.graphql';

export const resolveLocation = (isoCode) => {
    if (SPB_CODES.includes(isoCode)) return LOCATION_SPB;
    if (NIZ_CODES.includes(isoCode)) return LOCATION_NIZ;
    if (KZN_CODES.includes(isoCode)) return LOCATION_KZN;

    // backend might be not ready
    if (typeof isoCode === 'string') {
        if (isoCode.includes('TUL')) return LOCATION_TUL;
        if (isoCode.includes('YAR')) return LOCATION_YAR;
        if (isoCode.includes('VLA')) return LOCATION_VLA;
        if (isoCode.includes('VOR')) return LOCATION_VOR;
        if (isoCode.includes('RYA')) return LOCATION_RYA;
        if (isoCode.includes('KLU')) return LOCATION_KLU;
        if (isoCode.includes('TVE')) return LOCATION_TVE;
    }
    return LOCATION_MSK;
};


const DEFAULT_LOCATION_CONTEXT = {
    locationKey: LOCATION_MSK,
    country_subdivision: 'RU-MOS',
    // eslint-disable-next-line no-unused-vars
    mutateAddreessHandler: (I) => { },
    // eslint-disable-next-line no-unused-vars
    getCityTitile: (I) => { },
    isLocationDataLoading: false,
};

export const LocationContext = createContext(DEFAULT_LOCATION_CONTEXT);

const resolveSubdivisionField = (addressValue) => {
    if (addressValue === 'г Москва') {
        return 'RU-MOS';
    }

    if (addressValue === 'г Санкт-Петербург') {
        return 'RU-SPE';
    }

    if (addressValue === 'г Нижний Новгород') {
        return 'RU-NIZ';
    }

    if (addressValue === 'г Казань') {
        return 'RU-TA';
    }

    if (addressValue === 'г Тула') {
        return 'RU-TUL';
    }

    if (addressValue === 'г Ярославль') {
        return 'RU-YAR';
    }

    if (addressValue === 'г Владимир') {
        return 'RU-VLA';
    }

    if (addressValue === 'г Воронеж') {
        return 'RU-VOR';
    }

    if (addressValue === 'г Рязань') {
        return 'RU-RYA';
    }

    if (addressValue === 'г Калуга') {
        return 'RU-KLU';
    }

    if (addressValue === 'г Тверь') {
        return 'RU-TVE';
    }

    return 'RU-MOS';
};

export const resolveLocationKey = (addressValue) => {
    if (addressValue === 'г Москва') {
        return LOCATION_MSK;
    }

    if (addressValue === 'г Санкт-Петербург') {
        return LOCATION_SPB;
    }

    if (addressValue === 'г Нижний Новгород') {
        return LOCATION_NIZ;
    }

    if (addressValue === 'г Казань') {
        return LOCATION_KZN;
    }

    if (addressValue === 'г Тула') {
        return LOCATION_TUL;
    }

    if (addressValue === 'г Ярославль') {
        return LOCATION_YAR;
    }

    if (addressValue === 'г Владимир') {
        return LOCATION_VLA;
    }

    if (addressValue === 'г Рязань') {
        return LOCATION_RYA;
    }

    if (addressValue === 'г Калуга') {
        return LOCATION_KLU;
    }

    if (addressValue === 'г Тверь') {
        return LOCATION_TVE;
    }

    return LOCATION_MSK;
};

export const withLocationContext = (Component) => (props) => (
    <LocationContext.Consumer>
        {(locationContext) => <Component {...props} locationContext={locationContext} />}
    </LocationContext.Consumer>
);

export const getCityTitile = (deliveryAddressData) => {
    const initialCityObj = {
        title: 'Москва',
        longTitle: 'Москва',
        isMsk: true,
        isSpb: false,
        isNiz: false,
        isKzn: false,
        isTul: false,
        isYar: false,
        isVla: false,
        isRya: false,
        isKlu: false,
        isTve: false,
        engTitle: 'Moscow',
    };

    try {
        const { deliveryAddress: { address: { country_subdivision: subdivision } } } = deliveryAddressData;

        if (['RU-MOS', 'RU-MOW'].includes(subdivision)) {
            return initialCityObj;
        }

        if (['RU-SPE', 'RU-LEN'].includes(subdivision)) {
            return {
                title: 'С-Петербург',
                longTitle: 'Cанкт-Петербург',
                isMsk: false,
                isSpb: true,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'St. Petersburg',
            };
        }

        if (['RU-NIZ', 'RU-NIO'].includes(subdivision)) {
            return {
                title: 'Н.Новгород',
                longTitle: 'Нижний Новгород',
                isMsk: false,
                isSpb: false,
                isNiz: true,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'Nizhny Novgorod',
            };
        }

        if (['RU-TA', 'RU-TAO'].includes(subdivision)) {
            return {
                title: 'Казань',
                longTitle: 'Казань',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: true,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'Kazan',
            };
        }
        if (['RU-TUL'].includes(subdivision)) {
            return {
                title: 'Тула',
                longTitle: 'Тула',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: true,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'Tula',
            };
        }

        if (['RU-YAR'].includes(subdivision)) {
            return {
                title: 'Ярославль',
                longTitle: 'Ярославль',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: true,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'Yaroslavl',
            };
        }

        if (['RU-VLA'].includes(subdivision)) {
            return {
                title: 'Владимир',
                longTitle: 'Владимир',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: true,
                isRya: false,
                isKlu: false,
                isTve: false,
                engTitle: 'Vladimir',
            };
        }

        if (['RU-RYA'].includes(subdivision)) {
            return {
                title: 'Рязань',
                longTitle: 'Рязань',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: true,
                isKlu: false,
                isTve: false,
                engTitle: 'Ryazan',
            };
        }

        if (['RU-KLU'].includes(subdivision)) {
            return {
                title: 'Калуга',
                longTitle: 'Калуга',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: true,
                isTve: false,
                engTitle: 'Kaluga',
            };
        }

        if (['RU-TVE'].includes(subdivision)) {
            return {
                title: 'Тверь',
                longTitle: 'Тверь',
                isMsk: false,
                isSpb: false,
                isNiz: false,
                isKzn: false,
                isTul: false,
                isYar: false,
                isVla: false,
                isRya: false,
                isKlu: false,
                isTve: true,
                engTitle: 'Tver',
            };
        }
        return initialCityObj;
    } catch (error) {
        errorService.log({
            source: 'client',
            text: 'Error getCityTitile in LocationProvider',
            scope: 'LocationProvider.js',
            error,
        });
        return initialCityObj;
    }
};

function LocationProvider(props) {
    const {
        children,
    } = props;

    const [locationState, setLocationState] = useState(DEFAULT_LOCATION_CONTEXT);
    const [isEventPush, setIsEventPush] = useState(false);


    // useEffect(() => {
    //     console.log('location', regionSubdivision);
    // }, [regionSubdivision]);

    const { loading: deliveryAddressLoading, data: addressData } = useQuery(FETCH_DELIVERY_ADDRESS_SESSION, {
        onCompleted({ deliveryAddress }) {
            const {
                address: {
                    country_subdivision: subdivision,
                    city,
                },
            } = deliveryAddress;

            if (!isEventPush) {
                analyticService.push({
                    eventName: 'Select_Region',
                    result: resolveLocation(subdivision),
                    source_name: 'default',
                });
                setIsEventPush(true);
            }
            setLocationState({
                locationKey: resolveLocation(subdivision),
                country_subdivision: subdivision,
                isLocationDataLoading: false,
            });
        },
        context: {
            message: 'root:init:LocationProvider',
        },
        fetchPolicy: 'cache-first',
        onError(error) {
            errorService.log({
                source: 'server',
                text: 'Error FETCH_DELIVERY_ADDRESS_SESSION in LocationProvider',
                scope: 'LocationProvider.js',
                error,
            });
        },
    });

    const [
        mutateAddreess,
        { loading: deliveryAddressMutationLoading },
    ] = useMutation(MUTATE_SHIPPING_ADDRESS, {
        onCompleted: ({ shipping }) => {
            const { address: { country_subdivision } } = shipping;
            if (locationState.country_subdivision !== country_subdivision) {
                /**
                 * @description set initial payment method
                 */
                const queryParams = queryString.parse(window.location.search);
                const paymentMethodFromUrl = queryParams.payment;
                paymentMethodVar('online');
                subscriptionTypeVar(paymentMethodFromUrl !== 'recurrent' ? 'singlePay' : 'subscription');
            }
            setLocationState((prev) => ({
                ...prev,
                country_subdivision,
                locationKey: resolveLocation(country_subdivision),
            }));
        },
        update(cache, { data }) {
            cache.writeQuery({
                query: FETCH_DELIVERY_ADDRESS_SESSION,
                data: {
                    deliveryAddress: {
                        address: data.shipping.address,
                        __typename: 'deliveryAddress',
                    },
                },
            });
            cache.writeQuery({
                query: BASKET_PAGE_ROOT_QUERY_INITIAL_COPY,
                data: {
                    cart: data.shipping.cart,
                },
            });
        },
        onError: (error) => {
            errorService.log({
                source: 'server',
                text: 'Error mutateAdreess in LocationProvider',
                scope: 'LocationProvider.js',
                error,
            });
        },

        refetchQueries: ({ data }) => {
            const { shipping: { address } } = data;
            const nextRegionSubdivision = address.country_subdivision?.replace(/-/, '_');

            return [
                {
                    query: gql`query PERIODS_BY_SUBDIVISION(
                        $subdivision: countrySubdivision!
                    ) {
                        menuSidebar(
                            subdivision: $subdivision
                        ) {
                             periods {
                                start
                                end
                                isVisibleInFilter
                                categories {
                                    id
                                    title
                                    description
                                }
                                tags {
                                    id
                                    title
                                    categoryId: category_id
                                    categoryName: category_name
                                    erp_id
                                }
                            }
                            subdivision
                        }
                        }
                    `,
                    variables: {
                        subdivision: regionSubdivisionVar()?.replace(/-/g, '_'),
                    },
                },
                {
                    query: FETCH_AVAILABLE_MENU_DATES,
                    fetchPolicy: 'cache-first',
                    context: {
                        message: 'root:refetch:LocationProvider',
                    },
                    variables: {
                        subdivision: nextRegionSubdivision,
                    },
                },
            ];
        },

        context: {
            message: 'root:update:LocationProvider',
        },
    });

    const mutateAddreessHandler = useCallback(
        async (payload, onCompleteAction = () => { }) => {
            try {
                mutateAddreess({
                    variables: { address: { ...payload } },
                    onCompleted: onCompleteAction,
                    fetchPolicy: 'no-cache',
                });
            } catch (error) {
                errorService.log({
                    source: 'client',
                    text: 'Error mutateAddreessHandler in LocationProvider',
                    scope: 'LocationProvider.js',
                    error,
                });
            }
        }, [mutateAddreess]);

    const isDataLoading = useMemo(
        () => (deliveryAddressLoading || deliveryAddressMutationLoading),
        [deliveryAddressLoading, deliveryAddressMutationLoading],
    );


    const currentSubdivision = useMemo(
        () => addressData?.deliveryAddress?.address?.country_subdivision || 'RU-MOS',
        [addressData?.deliveryAddress?.address?.country_subdivision],
    );

    useEffect(
        () => {
            setLocationState((prev) => ({
                ...prev,
                country_subdivision: currentSubdivision,
                locationKey: resolveLocation(currentSubdivision),
            }));
        },
        [currentSubdivision],
    );

    const contextValue = useMemo(() => ({
        ...locationState,
        mutateAddreessHandler,
        getCityTitile,
        isLocationDataLoading: isDataLoading,
    }), [locationState, mutateAddreessHandler, isDataLoading]);

    return (
        <LocationContext.Provider value={contextValue}>
            {children}
        </LocationContext.Provider>
    );
}

export default React.memo(LocationProvider);


LocationProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
